import { FC, SetStateAction, Dispatch } from 'react';
import { useQuery } from 'react-query';
import { Grid, Autocomplete, TextField } from '@mui/material';
import { FiltersLayout, FiltersButtons, SearchField, DateRangePicker } from '../../../components';
import { IAllUser, IDateRange, IDropdownResponse } from '../../../models';
import { getAllUsers, getHowAccessedTypes, getRecordTypes } from '../../../fetch';

interface IActivityFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean, clearKey?: string) => void;
  setHasAppliedFilters: Dispatch<SetStateAction<boolean>>;
  hasAppliedFilters: boolean;
  setSearchValue?: (val: { [key: string]: string }) => void;
  searchValue?: {
    [key: string]: string;
  };
  handleUsernameSearch?: (val: { [key: string]: string }) => void;
  handleDescriptionSearch?: (val: { [key: string]: string }) => void;
  handleDataSearch?: (val: { [key: string]: string }) => void;
  handleGlobalSearch?: (val: { [key: string]: string }) => void;
  selectedDateRange: IDateRange | null;
  setSelectedDateRange: Dispatch<SetStateAction<IDateRange>>;
  typeValues: IDropdownResponse[];
  setTypeValues: (val: IDropdownResponse[]) => void;
  inputTypeValue: string;
  setInputTypeValue: (val: string) => void;
  actionValues: IDropdownResponse[];
  setActionValues: (val: IDropdownResponse[]) => void;
  inputActionValue: string;
  setInputActionValue: (val: string) => void;
  userValue: IAllUser | null;
  setUserValue: (val: IAllUser) => void;
  inputUserValue: string;
  setInputUserValue: (val: string) => void;
}

export const ActivityFilters: FC<IActivityFilters> = ({
  isLoading,
  applyFilters,
  setHasAppliedFilters,
  hasAppliedFilters,
  handleUsernameSearch,
  handleDataSearch,
  handleDescriptionSearch,
  searchValue,
  setSearchValue,
  setSelectedDateRange,
  selectedDateRange,
  typeValues,
  setTypeValues,
  inputTypeValue,
  setInputTypeValue,
  actionValues,
  setActionValues,
  inputActionValue,
  setInputActionValue,
  handleGlobalSearch,
  inputUserValue,
  setInputUserValue,
  setUserValue,
  userValue
}) => {
  const { isLoading: isLoadingTypes, data: types } = useQuery<IDropdownResponse[], Error>(
    ['getRecordTypes'],
    () => getRecordTypes()
  );

  const { isLoading: isLoadingAccessedTypes, data: accessedTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['getHowAccessedTypes'], () => getHowAccessedTypes());

  const { isLoading: isLoadingAllUsers, data: allUsers } = useQuery<IAllUser[], Error>(
    ['getAllUsers'],
    () => getAllUsers()
  );

  return (
    <FiltersLayout>
      <Grid item xs={12} sm={6} md={4}>
        <DateRangePicker
          id="createdOnDateRange"
          label="Date"
          value={selectedDateRange}
          isDisabled={isLoading}
          onChange={value => {
            setSelectedDateRange(value);
          }}
          fullWidth
          inputSize="small"
        />
      </Grid>
      {handleGlobalSearch && (
        <Grid item xs={12} sm={6} md={4}>
          <SearchField
            isDisabled={isLoading}
            placeholder="Search across activity..."
            handleSearch={() => handleGlobalSearch(searchValue)}
            searchValue={searchValue?.search}
            handleClearSearch={() => {
              setSearchValue({
                ...searchValue,
                search: ''
              });
              applyFilters(false, 'search');
            }}
            setSearchValue={val =>
              setSearchValue({
                ...searchValue,
                search: val
              })
            }
          />
        </Grid>
      )}
      {handleDataSearch && (
        <Grid item xs={12} sm={6} md={4}>
          <SearchField
            isDisabled={isLoading}
            placeholder="Search data audit..."
            handleSearch={() => handleDataSearch(searchValue)}
            searchValue={searchValue?.changesSearch}
            handleClearSearch={() => {
              setSearchValue({
                ...searchValue,
                changesSearch: ''
              });
              applyFilters(false, 'changesSearch');
            }}
            setSearchValue={val =>
              setSearchValue({
                ...searchValue,
                changesSearch: val
              })
            }
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          multiple
          id="filter-type"
          value={typeValues}
          onChange={(event, newValue: IDropdownResponse[], reason) => {
            if (reason === 'clear') {
              applyFilters(false, 'recordTypes');
            }
            setTypeValues(newValue);
          }}
          inputValue={inputTypeValue}
          onInputChange={(event, newInputValue) => {
            setInputTypeValue(newInputValue);
          }}
          options={types ?? []}
          getOptionLabel={option => option.description}
          filterSelectedOptions
          renderOption={(props, option: IDropdownResponse) => {
            return (
              <li {...props} key={`${option.value}`}>
                {option.description}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              disabled={isLoading || isLoadingTypes}
              label={isLoading ? 'Loading...' : 'Type'}
              size="small"
              placeholder="Add Type"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          multiple
          id="filter-action"
          value={actionValues}
          onChange={(event, newValue: IDropdownResponse[], reason) => {
            if (reason === 'clear') {
              applyFilters(false, 'howAccessedTypes');
            }
            setActionValues(newValue);
          }}
          inputValue={inputActionValue}
          onInputChange={(event, newInputValue) => {
            setInputActionValue(newInputValue);
          }}
          options={accessedTypes ?? []}
          getOptionLabel={option => option.description}
          filterSelectedOptions
          renderOption={(props, option: IDropdownResponse) => {
            return (
              <li {...props} key={`${option.value}`}>
                {option.description}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              disabled={isLoading || isLoadingAccessedTypes}
              label={isLoading ? 'Loading...' : 'Action'}
              size="small"
              placeholder="Add Action"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          id="user-action"
          value={userValue}
          onChange={(event, newValue: IAllUser, reason) => {
            if (reason === 'clear') {
              applyFilters(false, 'username');
            }
            setUserValue(newValue);
          }}
          inputValue={inputUserValue}
          onInputChange={(event, newInputValue) => {
            setInputUserValue(newInputValue);
          }}
          options={allUsers ?? []}
          getOptionLabel={option => (option ? option?.displayName : '')}
          filterSelectedOptions
          renderOption={(props, option: IAllUser) => {
            return (
              <li {...props} key={`${option.userName}`}>
                {option.displayName} - {option.userName}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              disabled={isLoading || isLoadingAllUsers}
              label={isLoading ? 'Loading...' : 'User'}
              size="small"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5} xl={3}>
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSelectedDateRange && setSelectedDateRange(undefined);
            setSearchValue &&
              setSearchValue({
                userName: '',
                description: '',
                changesSearch: '',
                search: ''
              });
            setUserValue(null);
            setInputUserValue('');
            setInputActionValue('');
            setActionValues([]);
            setTypeValues([]);
            setInputTypeValue('');
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};
